import moment from 'moment';
import { AxiosError } from 'axios';
import { Business } from 'models/business';
import { UserIdentity } from 'hooks/api';
import { UserInfo, UserPreference } from 'models/user';
import { AuthService } from './auth';
import { apiV2 } from './axios';
import { getCompanyListByUserId } from './companies';

export const getCurrentUser = async (): Promise<UserInfo> => {
  const { data } = await apiV2.get<unknown, { data: UserInfo }>('users/me');
  return data;
};

export const getUser = async (id: string): Promise<UserInfo> => {
  const { data } = await apiV2.get<UserInfo>(`users/${id}`);
  return data;
};

export const upsertUser = async (params: any): Promise<{ data: UserInfo }> => {
  const { data } = await apiV2.post('users', params);
  return data;
};

export const updateUser = (params: any, userId?: string): Promise<never> => {
  const id = userId ?? AuthService.userId();
  const param = { ...params };
  if (param.dob) {
    param.dob = moment(params.dob).format('MM/DD/YYYY');
  }
  return apiV2.patch(`users/${id}`, param);
};

export const updateUserById = (
  id: string,
  params: Partial<UserInfo>,
): Promise<never> =>
  apiV2.patch<Partial<UserInfo>, never>(`users/${id}`, params);

export const getUserIdentity = async (userId: string | undefined) => {
  const id = userId ?? AuthService.userId();
  try {
    const { data } = await apiV2.get<UserIdentity>(`households/${id}/head/ssn`);
    return data;
  } catch (error) {
    if ((error as AxiosError).response) {
      throw new Error((error as AxiosError<Error>).response?.data?.message);
    } else {
      throw new Error('An error occurred while fetching user identity.');
    }
  }
};

export const getUserCompanies = async (id: string): Promise<Business[]> => {
  const data = await getCompanyListByUserId(id);
  if (data != null) {
    return data;
  }
  return [];
};

export const getCurrentUserCompanies = async (): Promise<Business[]> => {
  const userId = AuthService.userId();
  return getUserCompanies(userId!);
};

export interface UserIdentityRequest {
  ssn: string;
}
export const updateIdentityByUserId = async (
  id: string,
  params: UserIdentityRequest,
): Promise<any> => {
  const { data } = await apiV2.put(`households/${id}/head/ssn`, params);
  return data;
};

export interface UserRoleRequest {
  role: string;
}

export const updateUserRole = async (
  id: string,
  params: UserRoleRequest,
): Promise<never> => apiV2.put(`users/${id}/roles`, params);

export interface UserEmailRequest {
  email: string;
}

export const updateUserEmail = async (
  id: string,
  params: UserEmailRequest,
): Promise<never> => apiV2.patch(`users/${id}/email`, params);

export const UsersService = {
  getCurrentUser,
  getCurrentUserCompanies,
  getUser,
  getUserCompanies,
  getUserIdentity,
  updateUserById,
  updateIdentityByUserId,
  upsertUser,
  updateUserRole,
  updateUserEmail,
};

export const resetUserMFA = async (userId: string) => {
  const { data } = await apiV2.post(`users/${userId}/reset-mfa`);
  return data;
};

export const disableUserMFA = async (userId: string) => {
  const { data } = await apiV2.post(`users/${userId}/disable-mfa`);
  return data;
};

export const enableUserMFA = async (userId: string) => {
  const { data } = await apiV2.post(`users/${userId}/enable-mfa`);
  return data;
};

export const getUserPreference = async (id: string) => {
  const { data } = await apiV2.get<UserPreference>(
    `users/${id}/preferences`,
  );
  return data;
};

export interface UpdateUserPreferencePayload {
  textNotify: boolean;
  phoneNumber: string;
  emailNotification?: {
    productCommunication?: boolean;
    incorporationUpdate?: boolean;
    bankingUpdate?: boolean;
  };
}

export const updateUserPreference = async (
  id: string,
  payload: UpdateUserPreferencePayload,
) => {
  const { data } = await apiV2.put(`users/${id}/preferences`, payload);
  return data;
};
